import { useState, useEffect } from "react";
import { useHistory } from "react-router-dom";
import useEventStore from "../Store/EventStore.jsx";

/**
 * useEditableCollection - Shared hook for editable collections (Events and Spaces)
 * 
 * Manages edit state and API calls for Events and Spaces, eliminating duplicate logic
 * between EventCard and SpaceCard components.
 * 
 * @param {string} type - Collection type ("events" or "spaces")
 * @param {Object} data - Collection data object
 * @param {string} data.eventName - Event name (for events)
 * @param {string} data.spaceName - Space name (for spaces)
 * @param {string} data.eventDescription - Event description (for events, optional)
 * @param {string} data.eventDate - Event date (for events, optional)
 * @param {string} data.giftID - Gift ID
 * 
 * @returns {Object} Edit state and handlers
 * @returns {boolean} returns.isEditing - Whether currently in edit mode
 * @returns {Object} returns.editedData - Current edited data
 * @returns {Function} returns.setEditedData - Setter for edited data
 * @returns {Function} returns.handleEdit - Handler for edit/save action
 * @returns {Function} returns.handleInputChange - Handler for input changes
 * @returns {Function} returns.setIsEditing - Setter for edit mode
 * 
 * @example
 * // For Events
 * const { isEditing, editedData, handleEdit, handleInputChange } = useEditableCollection(
 *   "events",
 *   eventData
 * );
 * 
 * @example
 * // For Spaces
 * const { isEditing, editedData, handleEdit } = useEditableCollection(
 *   "spaces",
 *   spaceData
 * );
 */
export const useEditableCollection = (type, data) => {
    const history = useHistory();
    const { handleEdit: handleEditApi } = useEventStore();

    const [isEditing, setIsEditing] = useState(false);

    // Determine the name field based on type
    const nameField = type === "events" ? "eventName" : "spaceName";
    const originalName = data[nameField];

    // Initialize edited data based on type
    const [editedData, setEditedData] = useState(() => {
        if (type === "events") {
            return {
                eventName: data.eventName || "",
                eventDescription: data.eventDescription || "",
                eventDate: data.eventDate || "",
            };
        } else {
            return {
                spaceName: data.spaceName || "",
            };
        }
    });

    // Update edited data when source data changes
    useEffect(() => {
        if (type === "events") {
            setEditedData({
                eventName: data.eventName || "",
                eventDescription: data.eventDescription || "",
                eventDate: data.eventDate || "",
            });
        } else {
            setEditedData({
                spaceName: data.spaceName || "",
            });
        }
    }, [data, type]);

    /**
     * Handle edit/save action
     * @param {Event} e - Click event
     */
    const handleEdit = async (e) => {
        e?.stopPropagation(); // Prevent redirect on button click

        if (isEditing) {
            try {
                // Get the new name based on type
                const newName = type === "events" ? editedData.eventName : editedData.spaceName;

                // Update the data object
                data[nameField] = newName;

                // Call API to save changes
                const response = await handleEditApi(type, originalName, editedData);

                if (response) {
                    console.log(`${type === "events" ? "Event" : "Space"} details updated successfully`);
                    // URL update removed to prevent unwanted redirect
                    setIsEditing(false);
                    return response;
                } else {
                    console.error(`Failed to update ${type === "events" ? "event" : "space"} details`);
                }
            } catch (error) {
                console.error(`Error updating ${type === "events" ? "event" : "space"} details:`, error);
            }
        } else {
            setIsEditing(true);
        }
    };

    /**
     * Handle input change for form fields
     * @param {Event} e - Change event
     */
    const handleInputChange = (e) => {
        e?.stopPropagation();
        const { name, value } = e.target;
        setEditedData((prev) => ({ ...prev, [name]: value }));
    };

    return {
        isEditing,
        editedData,
        setEditedData,
        handleEdit,
        handleInputChange,
        setIsEditing,
    };
};

export default useEditableCollection;
